import { useState, useEffect, useCallback } from 'react'
import { fetchDrillDetail, fetchDrillExportJson } from './api'

/**
 * DrillPanel — Slide-out panel showing detail rows behind a chart bar or observation.
 * 
 * Props:
 *   customerId  — customer slug
 *   queryId     — drilldown query ID (e.g., "profile:dept_headcount")
 *   filters     — optional filter object (e.g., { orgLevel3Code: "145" })
 *   title       — panel header (e.g., "145 - Client Services")
 *   subtitle    — optional subheader (e.g., "330 employees")
 *   onClose     — callback to close the panel
 */
export default function DrillPanel({ customerId, queryId, filters, title, subtitle, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSQL, setShowSQL] = useState(false)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (!queryId || !customerId) return
    setLoading(true)
    setError(null)
    fetchDrillDetail(customerId, queryId, filters, 200)
      .then(res => {
        if (res && res.success) {
          setData(res)
        } else {
          setError(res?.error || 'Failed to load data')
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [customerId, queryId, JSON.stringify(filters)])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const res = await fetchDrillExportJson(customerId, queryId, filters)
      if (res && res.csv) {
        const blob = new Blob([res.csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = res.filename || 'export.csv'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e) {
      console.error('Export failed:', e)
    }
    setExporting(false)
  }, [customerId, queryId, filters])

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="dp-overlay" onClick={onClose}>
      <div className="dp-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="dp-header">
          <div className="dp-header-left">
            <div className="dp-title">{title || 'Detail View'}</div>
            {subtitle && <div className="dp-subtitle">{subtitle}</div>}
            {filters && Object.keys(filters).length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                {Object.entries(filters).map(([k, v]) => (
                  <span key={k} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', background: 'var(--green-light)', color: 'var(--green-dark)', padding: '2px 7px', borderRadius: 3, fontWeight: 600 }}>
                    {k} = {String(v)}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="dp-header-actions">
            <button className="dp-btn dp-btn-sql" onClick={() => setShowSQL(!showSQL)} title="Show SQL">
              {showSQL ? 'Hide SQL' : 'SQL'}
            </button>
            <button className="dp-btn dp-btn-export" onClick={handleExport} disabled={exporting || !data} title="Export CSV">
              {exporting ? 'Exporting…' : '↓ Export'}
            </button>
            <button className="dp-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* SQL viewer */}
        {showSQL && data?.sql && (
          <div className="dp-sql">
            <pre>{data.sql}</pre>
          </div>
        )}

        {/* Content */}
        <div className="dp-body">
          {loading && <div className="dp-loading">Loading records…</div>}
          {error && <div className="dp-error">{error}</div>}
          {data && !loading && (
            <>
              <div className="dp-meta">
                {data.total_count != null && (
                  <span>{data.total_count.toLocaleString()} total records</span>
                )}
                {data.total_count > 200 && (
                  <span className="dp-meta-note">· showing first 200</span>
                )}
              </div>
              {data.data && data.data.length > 0 ? (
                <div className="dp-table-wrap">
                  <table className="dp-table">
                    <thead>
                      <tr>
                        {(data.columns || Object.keys(data.data[0])).map(c => (
                          <th key={c}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.data.map((row, i) => (
                        <tr key={i}>
                          {(data.columns || Object.keys(row)).map(c => (
                            <td key={c}>{row[c] != null ? String(row[c]) : '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="dp-empty">No records found</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
